import { useState, useRef, useCallback } from "react";
import { Heart, MoreVertical, Trash2, X } from "lucide-react";
import LikeButton from "./LikeButton";
import ShareButton from "./ShareButton";
import CommentSection from "./CommentSection";
import SaveButton from "./SaveButton";
import ImageCarousel from "./ImageCarousel";
import LinksAndSourcesSheet from "../Feed/LinksAndSourcesSheet";
import useLike from "../../hooks/useLike";
import { useAuth } from "../AuthPage/AuthContext";
import axiosInstance from "../../APIs/axiosInstance";
import { trackEvent } from "../../utils/analytics";

const Actions = ({
  id, image, images, username, caption,
  is_liked, likes_count, comment_count, is_bookmarked,
  postAuthorId, links,
  onDelete,
}) => {
  const { liked, likeCount, loading, handleLike } = useLike(id, is_liked, likes_count);
  const { user: currentUser } = useAuth();
  const isAuthor = currentUser?.username === username;

  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleLikeWithTracking = useCallback(() => {
    handleLike();
    trackEvent('like_action', { postId: id });
  }, [handleLike, id]);

  const lastTapRef = useRef(0);
  const [showHeart, setShowHeart] = useState(false);
  const [heartPos, setHeartPos] = useState({ x: "50%", y: "50%" });

  const allImages = (images && images.length > 0)
    ? images
    : (image ? [image] : []);

  const handleDoubleTap = useCallback((e) => {
    const now = Date.now();
    if (now - lastTapRef.current < 380) {
      if (!liked) handleLike();
      const rect = e.currentTarget.getBoundingClientRect();
      const clientX = e.changedTouches?.[0]?.clientX ?? e.clientX;
      const clientY = e.changedTouches?.[0]?.clientY ?? e.clientY;
      setHeartPos({
        x: `${clientX - rect.left}px`,
        y: `${clientY - rect.top}px`,
      });
      setShowHeart(true);
      setTimeout(() => setShowHeart(false), 900);
    }
    lastTapRef.current = now;
  }, [liked, handleLike]);

  const handleDeletePost = async () => {
    setDeleting(true);
    try {
      await axiosInstance.delete(`/posts/${id}/`);
      onDelete?.(id);
    } catch (err) {
      console.error("Delete post failed:", err);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">

      {/* ── Image — edge-to-edge, fills remaining height, double-tap zone ── */}
      <div
        className="flex-1 min-h-0 w-full relative post-img-area"
        onTouchEnd={handleDoubleTap}
        onDoubleClick={handleDoubleTap}
      >
        <ImageCarousel images={allImages} alt={username || "post"} />

        {/* Heart pop animation */}
        {showHeart && (
          <div
            className="absolute pointer-events-none z-20"
            style={{ left: heartPos.x, top: heartPos.y, transform: "translate(-50%, -50%)" }}
          >
            <Heart
              className="w-20 h-20 text-red-500 fill-red-500 animate-heart-pop"
              style={{ filter: "drop-shadow(0 0 16px rgba(239,68,68,0.9))" }}
            />
          </div>
        )}

        {/* Author-only: menu button */}
        {isAuthor && (
          <div className="absolute top-3 right-3 z-10">
            <button
              onClick={() => setShowMenu(v => !v)}
              className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm
                flex items-center justify-center border border-white/10
                hover:bg-black/70 transition-all"
            >
              <MoreVertical className="w-4 h-4 text-white/80" />
            </button>

            {showMenu && (
              <>
                {/* Backdrop to close menu */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-10 z-20 bg-[#1c1c1c] rounded-xl
                  border border-white/10 shadow-xl shadow-black/60 overflow-hidden min-w-[140px]">
                  <button
                    onClick={() => { setShowMenu(false); setShowDeleteConfirm(true); }}
                    className="w-full flex items-center gap-2.5 px-4 py-3
                      hover:bg-red-500/10 transition-colors text-sm text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete post
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Action bar — single row ── */}
      <div className="flex items-center justify-between px-3 pt-1
        pb-[calc(90px+env(safe-area-inset-bottom)+8px)]">

        {/* Left: Like · Comment · Share · Save */}
        <div className="flex items-center gap-0.5">
          <LikeButton
            liked={liked}
            likeCount={likeCount}
            loading={loading}
            onLike={handleLikeWithTracking}
          />
          <CommentSection postId={id} commentCount={comment_count} />
          <ShareButton postId={id} caption={caption} />
          <SaveButton postId={id} isBookmarked={is_bookmarked} />
        </div>

        {/* Right: Links & Sources */}
        <LinksAndSourcesSheet
          postId={id}
          postAuthorId={postAuthorId}
          links={links || []}
        />

      </div>

      {/* ── Delete confirm dialog ── */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]
          flex items-center justify-center px-6">
          <div className="bg-[#1c1c1c] rounded-2xl border border-white/10 p-6 w-full max-w-xs">
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-white font-semibold">Delete post?</h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="p-1 rounded-full hover:bg-white/10 transition -mt-0.5"
              >
                <X className="w-4 h-4 text-white/40" />
              </button>
            </div>
            <p className="text-white/40 text-sm mb-5">
              This post will be permanently deleted for everyone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 rounded-xl bg-white/10 text-white text-sm font-medium
                  hover:bg-white/20 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePost}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium
                  hover:bg-red-600 transition disabled:opacity-50"
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Actions;
